
import $ from "jquery"
import isBlankStr from "underscore.string/isBlank"
import Promise from "plugins/bluebird/index"

import DomIdGenerator from "app/utils/dom_id_generator"
import Notifier from "app/notifier"
import {captureException} from "app/error_reporting"
import GoogleMaps_asyncLoad from "app/google-maps"

import SwitchMode from "components/widgets/switch_mode"


import I18n from "plugins/i18n-js"


const RejectionReasons = {
  FAILURE_CASE_HANDLED: "FAILURE_CASE_HANDLED",
}


interface Selector {
  scope:  string
  street: string
}

interface InputSelector {
  scope?:  string
  street?: string
}

const default_selector = {
  scope:  ".cell--listings--pictures-and-map-browser--tabbed",
  street: ".js-google-street-view",
}


class GoogleStreetDiv {
  private selector: Selector

  private $scope: JQuery
  private $street_view_box: JQuery

  private panoramas_per_dom_id: {
    [index: string]: google.maps.StreetViewPanorama
  }

  constructor(selector?: InputSelector) {
    this.selector = Object.assign({}, default_selector, selector)
    this.$scope = $(this.selector.scope)
    this.$street_view_box = this.$scope.find(this.selector.street)

    this.panoramas_per_dom_id = {}
  }

  public create_with_promise(): Promise<void> {
    return this.created().then(() => {
      this.bind_action()
      // Since there are visitors switched to street view before
      // Promise resolved
      // We need to try to display after load
      // `set_street_view_visible` will do nothing when container invisible
      this.set_street_view_visible()
    })
  }

  private created(){
    return GoogleMaps_asyncLoad()
  }
  private bind_action(){
    SwitchMode.on("switch_mode:mode_switched", () => {
      this.set_street_view_visible()
    })

    return this
  }
  private set_street_view_visible(){
    this.$street_view_box.each((_idx, street_view_box) => {
      const $street_view_box = $(street_view_box)

      if ($street_view_box.is(":hidden")) { return }

      let dom_id = street_view_box.id
      if (isBlankStr(street_view_box.id)) {
        dom_id = DomIdGenerator.generateNewId()
        street_view_box.setAttribute("id", dom_id)
      }

      const existing_panorama = this.panoramas_per_dom_id[dom_id]

      const panorama_promise: Promise<google.maps.StreetViewPanorama> = Promise.resolve()
      .then(() => {
        if (existing_panorama != null) {
          return existing_panorama
        }

        return GoogleMaps_asyncLoad()
        .then((google_maps) => {
          // # `getAttribute` is used instead of `dataset` for better compatibility
          // # Both return string
          const lat_str = street_view_box.getAttribute("data-lat")
          const lng_str = street_view_box.getAttribute("data-lng")
          if (lat_str == null || lng_str == null) {
            Notifier.showError(I18n.t("js.app.street_view.errors.street_view_unavailable_at_this_location"))
            throw RejectionReasons.FAILURE_CASE_HANDLED
          }

          const lat = parseFloat(lat_str)
          const lng = parseFloat(lng_str)
          const latlng_obj = new google_maps.LatLng(lat, lng)

          const sv = new google_maps.StreetViewService()

          return new Promise((resolve, reject) => {
            // Default is 50m
            sv.getPanorama({location: latlng_obj, radius: 50}, (data, status) => {
              if (status === google_maps.StreetViewStatus.OK) {
                const location_pano = (data == null || data.location == null) ? null : data.location.pano
                if (location_pano == null) {
                  Notifier.showError(I18n.t("js.app.street_view.errors.street_view_unavailable_at_this_location"))
                  reject(RejectionReasons.FAILURE_CASE_HANDLED)
                  return
                }

                const new_panorama = new google_maps.StreetViewPanorama(
                  street_view_box,
                )
                new_panorama.setPano(location_pano)
                new_panorama.setPov({
                  heading:  0,
                  pitch:    0,
                })

                this.panoramas_per_dom_id[dom_id] = new_panorama

                resolve(new_panorama)
                return
              }

              Notifier.showError(I18n.t("js.app.street_view.errors.street_view_unavailable_at_this_location"))
              reject(RejectionReasons.FAILURE_CASE_HANDLED)
            })
          })
        })
      })

      panorama_promise.then((panorama) => {
        panorama.setVisible(true)
      })
      .catch((reason_or_error) => {
        if (reason_or_error === RejectionReasons.FAILURE_CASE_HANDLED) {
          return
        }

        captureException(reason_or_error)
      })
      .done()
    })
  }
}


const factory = {
  create_with_promise(): Promise<void> {
    return new Promise((resolve) => {
      const x = new GoogleStreetDiv()
      x.create_with_promise().then(() => {
        resolve()
      })
      .done()
    })
  },
}

export default factory
